// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import Tab from "@insite/mobius/Tab/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup/TabGroup";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@sc/App/Components/Common/XngZone";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";

interface TabsStyles {
    tabsContainer?: TabGroupPresentationProps;
    gridItem?: InjectableCss;
}

export const tabsStyles: TabsStyles = {};

const styles = tabsStyles;

const enum tabFields {
    title = "title",
}

const enum fields {
    tabs = "tabs",
}

interface TabField {
    fields: {
        [tabFields.title]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.tabs]: TabField[];
    };
}
type Props = OwnProps;

const TabbedContainer: FC<Props> = ({ fields: { tabs = [] }, id }) => {
    const isEditing = useShellContext().isEditing;
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const [tabsState, setTabsState] = useState<TabField[]>([]);
    const _tabsState = useAppSelector((state: ApplicationState) => (state as CustomAppState).xTabs, shallowEqual);
    const stillLoadingZones = useMemo(
        () => tabs.filter(tab => !_tabsState[`tab_${tab.fields.title}_${id}`]),
        [_tabsState],
    );

    useEffect(() => {
        if (isEditing) {
            setTabsState(tabs); // In case we change in CMS.
        }
    }, [isEditing, tabs]);

    useEffect(() => {
        if (!isEditing) {
            setTabsState(
                tabs.filter(tab => {
                    if (_tabsState[`tab_${tab.fields.title}_${id}`]) {
                        // make sure this zone is loaded!
                        return true;
                    }
                    const tabPanel = tabContainerRef.current?.querySelector(`[id="${tab.fields.title}"]`); // handle non-loading zones
                    return tabPanel?.children?.[0]?.children?.length;
                }),
            );
        }
    }, [_tabsState, isEditing]);

    return (
        <StyledWrapper {...styles.gridItem} ref={tabContainerRef} key={`tabsWrapper_${tabsState.length}`}>
            {/* load all tabs to select loaded once. */}
            {!isEditing && !!stillLoadingZones.length && (
                <Hidden hidden>
                    {stillLoadingZones.map(tab => (
                        <StyledWrapper className="tab" id={tab.fields.title} key={`hidden_${tab.fields.title}`}>
                            <Zone contentId={id} zoneName={`tab_${tab.fields.title}_${id}`} />
                        </StyledWrapper>
                    ))}
                </Hidden>
            )}
            {/* same zone name */}
            {!!tabsState.length && (
                <TabGroup {...styles.tabsContainer}>
                    {tabsState.map(tab => (
                        <Tab
                            key={tab.fields.title}
                            headline={tab.fields.title}
                            tabKey={tab.fields.title}
                            aria-label={`${tab.fields.title} tab`}
                        >
                            <XngZone
                                contentId={id}
                                zoneName={`tab_${tab.fields.title}_${id}`}
                                hint={`Zone for ${tab.fields.title}`}
                            />
                        </Tab>
                    ))}
                </TabGroup>
            )}
        </StyledWrapper>
    );
};
const xTabWidget: WidgetModule = {
    component: TabbedContainer,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        displayName: "Tabbed Container",
        fieldDefinitions: [
            {
                editorTemplate: "ListField",
                name: fields.tabs,
                getDisplay: (item: any) => item.fields.title,
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        displayName: "Title",
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};
export default xTabWidget;
